import { useEffect, useState } from "react";
import { BookOpen, FileCheck2, Layers, ExternalLink, Plus, Trash2 } from "lucide-react";
import { api, API, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHero } from "@/components/PageHero";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function FontiNormative() {
  const { user } = useAuth();
  const isSuper = !!user?.super_admin;
  const [norms, setNorms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [sigla, setSigla] = useState("");
  const [titolo, setTitolo] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const order = ["ISO 9001", "ISO 45001", "SA8000", "UNI", "PdR"];
  const rank = (s) => { const i = order.findIndex((o) => (s || "").includes(o)); return i === -1 ? 99 : i; };
  const load = () => {
    setLoading(true);
    api.get("/norms")
      .then((r) => { setNorms([...r.data].sort((a, b) => rank(a.sigla) - rank(b.sigla))); setError(false); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async () => {
    if (!sigla.trim() || !file) { toast.error("Inserisci sigla e file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("sigla", sigla.trim());
      fd.append("titolo", titolo.trim());
      fd.append("file", file);
      await api.post("/norms", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fonte normativa aggiunta");
      setOpen(false); setSigla(""); setTitolo(""); setFile(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const remove = async (n) => {
    if (!window.confirm(`Eliminare la fonte ${n.sigla}?`)) return;
    try { await api.delete(`/norms/${n.id}`); toast.success("Fonte eliminata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHero label="Base di conoscenza" title="Fonti normative"
        subtitle={<>Norme e standard di riferimento su cui <strong>Steve</strong> verifica la coerenza dei documenti (ISO, UNI, prassi di riferimento).</>}>
        {isSuper && (
          <Button onClick={() => setOpen(true)} data-testid="add-norm-btn"
            className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
            <Plus className="h-4 w-4 mr-2" /> Aggiungi fonte
          </Button>
        )}
      </PageHero>

      {loading ? (
        <p className="text-sm text-slate-500">Caricamento fonti...</p>
      ) : error ? (
        <Card className="p-12 text-center border-dashed border-red-200 bg-red-50/50" data-testid="norms-error">
          <BookOpen className="h-10 w-10 text-red-300 mx-auto mb-3" />
          <p className="text-slate-700 font-medium">Impossibile caricare le fonti normative</p>
          <p className="text-sm text-slate-500 mt-1">Riprova più tardi o verifica i permessi di accesso.</p>
        </Card>
      ) : norms.length === 0 ? (
        <Card className="p-12 text-center border-dashed" data-testid="norms-empty">
          <BookOpen className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-700 font-medium">Nessuna fonte normativa caricata</p>
          <p className="text-sm text-slate-500 mt-1">
            {isSuper ? 'Usa "Aggiungi fonte" per caricare una norma (PDF) e abilitare i controlli di Steve.'
                     : "Le fonti vengono ingerite dall'amministratore per abilitare i controlli di coerenza ISO."}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="norms-list">
          {norms.map((n) => (
            <Card key={n.id} className="p-5 border-slate-200 flex gap-4" data-testid={`norm-${n.id}`}>
              <div className="h-11 w-11 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                <FileCheck2 className="h-6 w-6 text-blue-700" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 font-mono mb-2">{n.sigla}</Badge>
                  {isSuper && (
                    <button onClick={() => remove(n)} data-testid={`norm-delete-${n.id}`}
                      className="text-slate-300 hover:text-red-500 transition-colors" aria-label="Elimina fonte">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <h3 className="font-display font-medium leading-snug">{n.titolo}</h3>
                <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                  <Layers className="h-3.5 w-3.5 text-slate-500" />
                  {n.chars ? `${n.chars.toLocaleString("it-IT")} caratteri indicizzati` : "Contenuto indicizzato"}
                </div>
                <a href={`${API}/norms/${n.id}/file`} target="_blank" rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800 transition-colors"
                  data-testid={`norm-open-${n.id}`}>
                  <ExternalLink className="h-3.5 w-3.5" /> Apri PDF / Consulta fonte
                </a>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Aggiungi fonte normativa</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Sigla *</Label>
              <Input value={sigla} onChange={(e) => setSigla(e.target.value)}
                placeholder="Es. UNI CEI EN ISO/IEC 27001:2024" className="mt-1.5" data-testid="norm-sigla" />
            </div>
            <div>
              <Label>Titolo</Label>
              <Input value={titolo} onChange={(e) => setTitolo(e.target.value)}
                placeholder="Descrizione della norma" className="mt-1.5" data-testid="norm-titolo" />
            </div>
            <div>
              <Label>File (PDF, DOCX, TXT) *</Label>
              <Input type="file" accept=".pdf,.docx,.txt" onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1.5" data-testid="norm-file" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={submit} disabled={uploading} data-testid="norm-upload-submit">
                {uploading ? "Caricamento…" : "Carica"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
